/**
 * Procedural somatic sound design for the shared entrainment bus.
 *
 * The field is intentionally quiet and additive: a rounded low-frequency
 * lub-dub plus a filtered air wash that traces inhale and exhale. It does not
 * replace music, narration, binaural beats, subliminals, or the existing
 * polytemporal layer. Running in an AudioWorklet keeps both envelopes sample
 * accurate and lets AudioContext suspend/resume freeze every effect together.
 */
export const DEFAULT_SOMATIC_SOUNDS = Object.freeze({
  enabled: true,
  clockEpoch: 0,
  heartbeatEnabled: true,
  // Bluetooth speakers commonly attenuate the lowest octave. Use a rounded
  // low body pulse with a restrained second harmonic so the rhythm is audible
  // on speakers without turning into a click or a bright drum hit.
  heartbeatLevel: 0.028,
  heartbeatBpm: 36,
  heartbeatCarrierHz: 70,
  heartbeatDoubletMs: 220,
  heartbeatHarmonicRatio: 0.32,
  breathSoundEnabled: true,
  breathSoundLevel: 0.014,
  breathInSeconds: 3.5,
  breathOutSeconds: 4,
  breathAirColor: "soft",
});

const clamp = (value, minimum, maximum, fallback) => {
  const number = Number(value);
  return Number.isFinite(number) ? Math.max(minimum, Math.min(maximum, number)) : fallback;
};

export function normalizeSomaticSoundscape(value = {}) {
  const defaults = DEFAULT_SOMATIC_SOUNDS;
  const color = ["soft", "balanced", "airy"].includes(String(value.breathAirColor))
    ? String(value.breathAirColor)
    : defaults.breathAirColor;
  return {
    enabled: value.enabled === undefined ? defaults.enabled : Boolean(value.enabled),
    clockEpoch: Number.isFinite(Number(value.clockEpoch)) ? Number(value.clockEpoch) : defaults.clockEpoch,
    heartbeatEnabled: value.heartbeatEnabled === undefined
      ? defaults.heartbeatEnabled : Boolean(value.heartbeatEnabled),
    heartbeatLevel: clamp(value.heartbeatLevel, 0, 0.05, defaults.heartbeatLevel),
    heartbeatBpm: clamp(value.heartbeatBpm ?? value.bpm, 24, 72, defaults.heartbeatBpm),
    heartbeatCarrierHz: clamp(value.heartbeatCarrierHz, 36, 96, defaults.heartbeatCarrierHz),
    heartbeatDoubletMs: clamp(value.heartbeatDoubletMs, 110, 260, defaults.heartbeatDoubletMs),
    heartbeatHarmonicRatio: clamp(value.heartbeatHarmonicRatio, 0, 0.6, defaults.heartbeatHarmonicRatio),
    breathSoundEnabled: value.breathSoundEnabled === undefined
      ? defaults.breathSoundEnabled : Boolean(value.breathSoundEnabled),
    breathSoundLevel: clamp(value.breathSoundLevel, 0, 0.03, defaults.breathSoundLevel),
    breathInSeconds: clamp(value.breathInSeconds, 2.5, 5, defaults.breathInSeconds),
    breathOutSeconds: clamp(value.breathOutSeconds, 2.5, 6, defaults.breathOutSeconds),
    breathAirColor: color,
  };
}

export function somaticSceneSettings(scene = {}) {
  const authoredInhale = Number(scene.breathInSeconds);
  const authoredExhale = Number(scene.breathOutSeconds);
  return normalizeSomaticSoundscape({
    enabled: scene.somaticSoundEnabled !== false,
    clockEpoch: scene.clockEpoch,
    heartbeatEnabled: scene.heartbeatAudioEnabled !== false,
    heartbeatLevel: scene.heartbeatAudioLevel,
    heartbeatBpm: scene.heartbeatBpm ?? scene.bpm,
    heartbeatCarrierHz: scene.heartbeatCarrierHz,
    heartbeatDoubletMs: scene.heartbeatDoubletMs,
    heartbeatHarmonicRatio: scene.heartbeatHarmonicRatio,
    breathSoundEnabled: scene.breathSoundEnabled !== false,
    breathSoundLevel: scene.breathSoundLevel,
    breathInSeconds: authoredInhale >= 2.5 ? authoredInhale : DEFAULT_SOMATIC_SOUNDS.breathInSeconds,
    breathOutSeconds: authoredExhale >= 2.5 ? authoredExhale : DEFAULT_SOMATIC_SOUNDS.breathOutSeconds,
    breathAirColor: scene.breathAirColor,
  });
}

// Self-contained because AudioWorkletGlobalScope cannot import app modules.
export const HYPNOTIC_SOUNDSCAPE_WORKLET_SOURCE = `
class HypnoticSomaticSoundscapeProcessor extends AudioWorkletProcessor {
  constructor() {
    super();
    this.s = ${JSON.stringify(DEFAULT_SOMATIC_SOUNDS)};
    this.startedAt = 0;
    this.started = false;
    this.narrationUntil = 0;
    this.narrationGain = 0.32;
    this.sampleIndex = 0;
    this.heartPhase = 0;
    this.breathPhase = 0;
    this.carrierPhase = 0;
    this.carrierPhase2 = 0;
    this.airLow = [0, 0];
    this.airBody = [0, 0];
    this.seed = 0x51f15e;
    this.port.onmessage = ({ data }) => {
      if (data?.type === "settings") this.s = { ...this.s, ...data.settings };
      if (data?.type === "start") {
        this.startedAt = Number(data.at) || currentTime;
        this.started = true;
      }
      if (data?.type === "narration") {
        this.narrationUntil = Math.max(currentTime, Number(data.until) || currentTime);
        this.narrationGain = Math.max(0.2, Math.min(0.5, Number(data.gain) || 0.32));
      }
    };
  }
  random() {
    let x = this.seed;
    x ^= x << 13; x ^= x >>> 17; x ^= x << 5;
    this.seed = x >>> 0;
    return this.seed / 4294967296;
  }
  thump(time, attack, decay) {
    if (time < 0 || time > attack + decay * 7) return 0;
    if (time < attack) return Math.sin((time / attack) * Math.PI * .5);
    return Math.exp(-(time - attack) / decay);
  }
  process(inputs, outputs) {
    const output = outputs[0];
    if (!output?.[0]) return true;
    const left = output[0];
    const right = output[1] || output[0];
    const sr = sampleRate;
    const twoPi = Math.PI * 2;
    const s = this.s;
    if (!this.started) {
      this.startedAt = Number(s.clockEpoch) || currentTime;
      this.started = true;
    }
    const heartPeriod = 60 / Math.max(24, Math.min(72, Number(s.heartbeatBpm) || 36));
    const doublet = Math.max(.11, Math.min(.26, Number(s.heartbeatDoubletMs) / 1000 || .165));
    const inhale = Math.max(2.5, Math.min(5, Number(s.breathInSeconds) || 3.5));
    const exhale = Math.max(2.5, Math.min(6, Number(s.breathOutSeconds) || 4));
    const breathCycle = inhale + exhale;
    const colorCutoff = s.breathAirColor === "airy" ? 2400 : s.breathAirColor === "balanced" ? 1600 : 1050;
    const airAlpha = 1 - Math.exp(-twoPi * colorCutoff / sr);
    const bodyAlpha = 1 - Math.exp(-twoPi * 170 / sr);
    const enabled = Boolean(s.enabled);
    for (let i = 0; i < left.length; i += 1) {
      const now = currentTime + i / sr;
      const elapsed = Math.max(0, now - this.startedAt);
      const heartTime = elapsed % heartPeriod;
      const lub = this.thump(heartTime, .075, .30);
      const dubTime = heartTime >= doublet ? heartTime - doublet : heartTime + heartPeriod - doublet;
      const dub = this.thump(dubTime, .060, .24) * .72;
      const heartEnvelope = lub + dub;
      const carrierHz = Math.max(36, Math.min(96, Number(s.heartbeatCarrierHz) || 70));
      this.carrierPhase += twoPi * carrierHz / sr;
      this.carrierPhase2 += twoPi * carrierHz * 2 / sr;
      if (this.carrierPhase > twoPi) this.carrierPhase -= twoPi;
      if (this.carrierPhase2 > twoPi) this.carrierPhase2 -= twoPi;
      const harmonicRatio = Math.max(0, Math.min(0.6, Number(s.heartbeatHarmonicRatio) || 0.32));
      const heartbeat = (
        Math.sin(this.carrierPhase) * (1 - harmonicRatio)
        + Math.sin(this.carrierPhase2) * harmonicRatio
      ) * heartEnvelope * (s.heartbeatEnabled ? Number(s.heartbeatLevel) || 0 : 0)
        * (now < this.narrationUntil ? this.narrationGain : 1);

      const breathTime = elapsed % breathCycle;
      const inPhase = breathTime < inhale;
      const local = inPhase ? breathTime / inhale : (breathTime - inhale) / exhale;
      // Smooth zero-slope ends prevent a rhythmic click at each breath turn.
      const arc = .5 - .5 * Math.cos(Math.PI * 2 * local);
      const breathEnvelope = Math.pow(Math.max(0, arc), .72) * (inPhase ? .62 : 1);
      const pan = Math.sin(twoPi * this.breathPhase / breathCycle) * .10;
      const gains = [Math.sqrt((1 - pan) * .5), Math.sqrt((1 + pan) * .5)];
      const breathLevel = (s.breathSoundEnabled ? Number(s.breathSoundLevel) || 0 : 0)
        * (now < this.narrationUntil ? this.narrationGain : 1);
      const breath = [0, 0];
      for (let channel = 0; channel < 2; channel += 1) {
        const white = this.random() * 2 - 1;
        this.airLow[channel] += airAlpha * (white - this.airLow[channel]);
        this.airBody[channel] += bodyAlpha * (white - this.airBody[channel]);
        const band = this.airLow[channel] - this.airBody[channel];
        breath[channel] = band * breathEnvelope * breathLevel * gains[channel];
      }
      const master = enabled ? 1 : 0;
      left[i] = (heartbeat * .707 + breath[0]) * master;
      right[i] = (heartbeat * .707 + breath[1]) * master;
    }
    return true;
  }
}
registerProcessor("hypnotic-somatic-soundscape", HypnoticSomaticSoundscapeProcessor);
`;

const registeredContexts = new WeakMap();

export async function ensureHypnoticSoundscapeWorklet(context) {
  if (!context?.audioWorklet || typeof AudioWorkletNode === "undefined") return false;
  if (registeredContexts.has(context)) return registeredContexts.get(context);
  const promise = (async () => {
    const blob = new Blob([HYPNOTIC_SOUNDSCAPE_WORKLET_SOURCE], { type: "text/javascript" });
    const url = URL.createObjectURL(blob);
    try {
      await context.audioWorklet.addModule(url);
      return true;
    } finally {
      URL.revokeObjectURL(url);
    }
  })().catch(() => false);
  registeredContexts.set(context, promise);
  return promise;
}

export class HypnoticSoundscape {
  constructor(context, destination = context?.destination) {
    this.context = context;
    this.destination = destination;
    this.node = null;
    this.settings = normalizeSomaticSoundscape();
  }

  async start(value = {}) {
    this.settings = normalizeSomaticSoundscape(value);
    if (this.node) return this.apply(this.settings);
    if (!await ensureHypnoticSoundscapeWorklet(this.context)) return false;
    this.node = new AudioWorkletNode(this.context, "hypnotic-somatic-soundscape", {
      numberOfInputs: 0,
      numberOfOutputs: 1,
      outputChannelCount: [2],
    });
      this.node.connect(this.destination);
      this.node.port.postMessage({ type: "settings", settings: this.settings });
      this.node.port.postMessage({
        type: "start",
        at: Number(this.settings.clockEpoch) || this.context.currentTime,
      });
      return true;
  }

  apply(value = {}) {
    this.settings = normalizeSomaticSoundscape({ ...this.settings, ...value });
    this.node?.port.postMessage({ type: "settings", settings: this.settings });
    return Boolean(this.node);
  }

  setNarrationWindow(seconds = 0, gain = 0.32) {
    if (!this.node || !this.context) return;
    const duration = Math.max(0, Math.min(180, Number(seconds) || 0));
    this.node.port.postMessage({
      type: "narration",
      until: this.context.currentTime + duration,
      gain,
    });
  }

  stop() {
    this.apply({ enabled: false });
  }

  destroy() {
    try { this.node?.disconnect(); } catch {}
    this.node = null;
  }
}
